import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { setOnUnauthorized } from '~/lib/api';

export interface User {
  id: string;
  email: string;
  name: string;
}

export interface OrgInfo {
  id: string;
  name: string;
  role: string;
}

interface AppState {
  user: User | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  organizationId: string;
  organizations: OrgInfo[];
  selectedBrandId: string | null;
  isLoading: boolean;

  setUser: (user: User | null, isSuperAdmin?: boolean) => void;
  setOrganizationId: (id: string) => void;
  setOrganizations: (orgs: OrgInfo[]) => void;
  setSelectedBrandId: (id: string | null) => void;
  setLoading: (isLoading: boolean) => void;
  logout: () => void;
}

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      immer((set) => ({
        user: null,
        isAuthenticated: false,
        isSuperAdmin: false,
        organizationId: '',
        organizations: [],
        selectedBrandId: null,
        isLoading: false,

        setUser: (user, isSuperAdmin) =>
          set((state) => {
            state.user = user;
            state.isAuthenticated = user !== null;
            state.isSuperAdmin = isSuperAdmin ?? false;
          }),

        setOrganizationId: (id) =>
          set((state) => {
            state.organizationId = id;
          }),

        setOrganizations: (orgs) =>
          set((state) => {
            state.organizations = orgs;
          }),

        setSelectedBrandId: (id) =>
          set((state) => {
            state.selectedBrandId = id;
          }),

        setLoading: (isLoading) =>
          set((state) => {
            state.isLoading = isLoading;
          }),

        logout: () =>
          set((state) => {
            state.user = null;
            state.isAuthenticated = false;
            state.isSuperAdmin = false;
            state.organizationId = '';
            state.organizations = [];
            state.selectedBrandId = null;
            if (typeof window !== 'undefined') {
              localStorage.removeItem('auth_token');
              document.cookie = 'auth-token=; path=/; max-age=0';
            }
          }),
      })),
      {
        name: 'app-storage',
        version: 5,
        migrate: (persisted) => ({
          organizationId: '',
          organizations: [],
          selectedBrandId: null,
          user: null,
          isAuthenticated: false,
          ...(persisted as Record<string, unknown>),
        }),
        partialize: (state) => ({
          organizationId: state.organizationId,
          organizations: state.organizations,
          selectedBrandId: state.selectedBrandId,
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          // isSuperAdmin intentionally NOT persisted — always set from /auth/me
        }),
      }
    ),
    { name: 'AppStore' }
  )
);

// Wire up 401 interceptor → auto-logout
setOnUnauthorized(() => useAppStore.getState().logout());
